// Small helpers for loading uploaded files into ready-to-draw HTMLImageElement
// instances, and for cover-fit drawing onto a canvas.

export const MAX_IMAGE_BYTES = 50 * 1024 * 1024; // 50MB
export const MAX_AUDIO_BYTES = 300 * 1024 * 1024; // 300MB

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error('No file provided'));
    if (!file.type.startsWith('image/')) {
      return reject(new Error('That file is not an image.'));
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return reject(new Error('Image is too large (max 50MB).'));
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url, name: file.name });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not decode that image file.'));
    };
    img.src = url;
  });
}

export function drawImageCover(ctx, img, x, y, w, h, scale = 1) {
  const imgRatio = img.width / img.height;
  const boxRatio = w / h;
  let sx, sy, sw, sh;
  if (imgRatio > boxRatio) {
    sh = img.height;
    sw = sh * boxRatio;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    sw = img.width;
    sh = sw / boxRatio;
    sx = 0;
    sy = (img.height - sh) / 2;
  }
  // Shrinking (scale < 1) shrinks the destination box around its own
  // center, revealing whatever is underneath -- used for the foreground
  // logo/watermark size control. scale=1 is the original full-cover fit.
  const dw = w * scale;
  const dh = h * scale;
  const dx = x + (w - dw) / 2;
  const dy = y + (h - dh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

export function drawImageContainScaled(ctx, img, x, y, w, h, scale = 1) {
  // "Contain" fit: show the WHOLE image, uncropped, sized to fit within
  // w x h while preserving the image's own aspect ratio, then scale that
  // contain-fitted box by `scale` around its own center. Used for the
  // foreground logo/watermark, which must never be cropped -- only
  // resized. scale=0 is invisible, scale=1 is the largest the image can
  // be without cropping, values above 1 grow past that (may overflow the
  // frame edges, which is intentional for a "bleed" look).
  const imgRatio = img.width / img.height;
  const boxRatio = w / h;
  let containW, containH;
  if (imgRatio > boxRatio) {
    containW = w;
    containH = w / imgRatio;
  } else {
    containH = h;
    containW = h * imgRatio;
  }
  const dw = containW * scale;
  const dh = containH * scale;
  if (dw <= 0 || dh <= 0) return;
  const dx = x + (w - dw) / 2;
  const dy = y + (h - dh) / 2;
  ctx.drawImage(img, 0, 0, img.width, img.height, dx, dy, dw, dh);
}

export function drawImageContain(ctx, img, cx, cy, size) {
  // Draw an image centered at (cx, cy) so it fully fills a `size` x `size`
  // square (cropping the longer side) — used for disc art on a circular mask.
  const half = size / 2;
  drawImageCover(ctx, img, cx - half, cy - half, size, size);
}
